import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../../../services/notification.service'; // NotificationService;
import { Subscription } from 'rxjs';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../../../../../../shared/components/simple-dropdown/simple-dropdown.component';
import {
  MultiSelectComponent,
  MultiSelectOption,
} from '../../../../../../shared/components/wi-multi-select/wi-multi-select.component';
import { LaneFormValues } from '../../../../../models/lane.model';
import { LaneService } from '../../../../../services/lane.service';
import { ApiService } from '../../../../../../services/api.service';
import {
  ALL_LANE_TRANSACTION_TYPE_IDS,
  LANE_TRANSACTION_TYPE_OPTIONS,
  areAllLaneTransactionTypesSelected,
  buildLanePayload,
  DEFAULT_GATE_CLOSE_PORT,
  DEFAULT_GATE_OPEN_PORT,
  gatePortError,
  parseGatePort,
  mapLaneSaveError,
  mapLaneToForm,
} from '../../../../../utils/lane.util';

const CLOSE_PORT_SAME_AS_OPEN = 'Must be different from the open relay port';

/** Keep only digits, so the port fields stay numeric whatever is typed or pasted. */
function digitsOnly(value: string | null | undefined): string {
  return String(value ?? '').replace(/\D/g, '');
}

@Component({
  selector: 'app-lane-form-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SimpleDropdownComponent, MultiSelectComponent],
  templateUrl: './lane-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./lane-form-modal.component.scss'],
})
export class LaneFormModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() laneId = '';
  @Input() companyOptions: DropdownOption[] = [];
  @Input() initialCompanyId = '';
  @Input() initialLocationId = '';
  @Input() lockCompanyLocation = false;

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<{ companyId: string; locationId: string }>();

  companyId = '';
  locationId = '';
  locationOptions: DropdownOption[] = [];
  laneName = '';
  hasCamera = false;
  cameraSerialNumber = '';
  cameraModel = '';
  cameraStatus: 'active' | 'inactive' = 'active';
  zone: 'type_1' | 'type_2' = 'type_1';
  laneType: 'automatic' | 'manual' = 'automatic';
  hasGate = false;
  gateRelay = DEFAULT_GATE_OPEN_PORT;
  gateSerialNumber = '';
  gateModel = '';
  closeGateEnabled = false;
  gateCloseRelay = DEFAULT_GATE_CLOSE_PORT;
  status: 'active' | 'inactive' = 'active';
  transactionTypeIds: string[] = [...ALL_LANE_TRANSACTION_TYPE_IDS];
  readonly transactionTypeOptions: MultiSelectOption[] = LANE_TRANSACTION_TYPE_OPTIONS.map(
    option => ({ id: option.id, name: option.name })
  );
  isCameraConnected = false;
  isGateConnected = false;
  isCheckingCameraConnection = false;
  isCheckingGateConnection = false;
  private cameraSerialCheckDebounce: any;
  private gateSerialCheckDebounce: any;
  private cameraConnectionSubscription: Subscription | null = null;
  private gateConnectionSubscription: Subscription | null = null;

  statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  gateTypeOptions: DropdownOption[] = [
    { label: 'Automatic', value: 'automatic' },
    { label: 'Manual', value: 'manual' },
  ];

  isCompanyDropdownOpen = false;
  isLocationDropdownOpen = false;
  isStatusDropdownOpen = false;
  isCameraStatusDropdownOpen = false;
  isGateTypeDropdownOpen = false;
  isSubmitting = false;
  isLoadingLane = false;
  laneNameError = '';
  cameraSerialError = '';
  gateRelayError = '';
  gateCloseRelayError = '';
  gateSerialError = '';
  transactionTypeError = '';

  private readonly laneService = inject(LaneService);
  private readonly messageService = inject(NotificationService);
  private readonly apiService = inject(ApiService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      this.resetForm();
      if (this.mode === 'edit' && this.laneId) {
        this.loadLaneForEdit();
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up any pending timers
    clearTimeout(this.cameraSerialCheckDebounce);
    clearTimeout(this.gateSerialCheckDebounce);

    // Cancel any in-flight requests
    this.cameraConnectionSubscription?.unsubscribe();
    this.gateConnectionSubscription?.unsubscribe();
  }

  get modalTitle(): string {
    return this.mode === 'edit' ? 'Edit lane' : 'Add lane';
  }

  get submitLabel(): string {
    return this.mode === 'edit' ? 'Save changes' : 'Add lane';
  }

  get laneTypeHelperText(): string {
    return this.laneType === 'automatic'
      ? 'The gate automatically opens for the payment types selected below, and for members recognized by LPR. Uncheck Cash to keep the gate closed for cash customers.'
      : 'The gate will have to be opened by the attendant from his/her tablet.';
  }

  get allTransactionTypesSelected(): boolean {
    return areAllLaneTransactionTypesSelected(this.transactionTypeIds);
  }

  get transactionTypeTriggerText(): string {
    return this.allTransactionTypesSelected ? 'All' : '{{count}} selected';
  }

  private resetForm(): void {
    this.companyId = this.initialCompanyId;
    this.locationId = this.initialLocationId;
    this.laneName = '';
    this.hasCamera = false;
    this.cameraSerialNumber = '';
    this.cameraModel = '';
    this.cameraStatus = 'active';
    this.zone = 'type_1';
    this.laneType = 'automatic';
    this.hasGate = false;
    this.gateRelay = DEFAULT_GATE_OPEN_PORT;
    this.gateSerialNumber = '';
    this.gateModel = '';
    this.closeGateEnabled = false;
    this.gateCloseRelay = DEFAULT_GATE_CLOSE_PORT;
    this.status = 'active';
    this.transactionTypeIds = [...ALL_LANE_TRANSACTION_TYPE_IDS];
    this.laneNameError = '';
    this.cameraSerialError = '';
    this.gateRelayError = '';
    this.gateCloseRelayError = '';
    this.gateSerialError = '';
    this.transactionTypeError = '';
    this.isSubmitting = false;
    this.isLoadingLane = false;
    this.isCompanyDropdownOpen = false;
    this.isLocationDropdownOpen = false;
    this.isStatusDropdownOpen = false;
    this.isCameraStatusDropdownOpen = false;
    this.isGateTypeDropdownOpen = false;
    this.isCameraConnected = false;
    this.isGateConnected = false;
    this.isCheckingCameraConnection = false;
    this.isCheckingGateConnection = false;
    clearTimeout(this.cameraSerialCheckDebounce);
    clearTimeout(this.gateSerialCheckDebounce);
    this.cameraConnectionSubscription?.unsubscribe();
    this.gateConnectionSubscription?.unsubscribe();
    this.cameraConnectionSubscription = null;
    this.gateConnectionSubscription = null;

    if (this.companyId && this.mode === 'create' && !this.lockCompanyLocation) {
      this.loadLocations(this.companyId, true);
    } else {
      this.locationOptions = [];
    }
  }

  private loadLaneForEdit(): void {
    if (!this.companyId || !this.locationId || !this.laneId) {
      return;
    }

    this.isLoadingLane = true;
    this.laneService.getLane(this.companyId, this.locationId, this.laneId).subscribe({
      next: response => {
        this.isLoadingLane = false;
        if (response.status && response.data) {
          const form = mapLaneToForm(response.data);
          this.applyFormValues(form);
        }
      },
      error: err => {
        this.isLoadingLane = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to load lane',
        });
        this.close.emit();
      },
    });
  }

  private applyFormValues(form: LaneFormValues): void {
    this.laneName = form.laneName;
    this.hasCamera = form.hasCamera;
    this.cameraSerialNumber = form.cameraSerialNumber;
    this.cameraModel = form.cameraModel;
    this.cameraStatus = form.cameraStatus;
    this.zone = form.zone;
    this.hasGate = form.hasGate;
    this.laneType = form.laneType;
    this.gateRelay = form.gateRelay;
    this.gateSerialNumber = form.gateSerialNumber;
    this.gateModel = form.gateModel;
    this.closeGateEnabled = form.closeGateEnabled;
    this.gateCloseRelay = form.gateCloseRelay;
    this.status = form.status;
    this.transactionTypeIds = [...form.transactionTypeIds];

    // Setting the serials in code does not fire (ngModelChange), so the inputs' own
    // checks never run for a loaded lane; without this both read "Not connected".
    if (this.hasCamera) {
      this.checkDeviceConnection('camera');
    }
    if (this.hasGate) {
      this.checkDeviceConnection('gate');
    }
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget && !this.isSubmitting && !this.isLoadingLane) {
      this.onClose();
    }
  }

  onClose(): void {
    if (this.isSubmitting || this.isLoadingLane) {
      return;
    }
    this.close.emit();
  }

  private closeOtherDropdowns(
    except?: 'company' | 'location' | 'status' | 'cameraStatus' | 'gateType'
  ): void {
    if (except !== 'company') {
      this.isCompanyDropdownOpen = false;
    }
    if (except !== 'location') {
      this.isLocationDropdownOpen = false;
    }
    if (except !== 'status') {
      this.isStatusDropdownOpen = false;
    }
    if (except !== 'cameraStatus') {
      this.isCameraStatusDropdownOpen = false;
    }
    if (except !== 'gateType') {
      this.isGateTypeDropdownOpen = false;
    }
  }

  toggleCompanyDropdown(): void {
    if (this.lockCompanyLocation) {
      return;
    }
    this.isCompanyDropdownOpen = !this.isCompanyDropdownOpen;
    if (this.isCompanyDropdownOpen) {
      this.closeOtherDropdowns('company');
    }
  }

  closeCompanyDropdown(): void {
    this.isCompanyDropdownOpen = false;
  }

  selectCompany(companyId: string): void {
    if (this.lockCompanyLocation) {
      return;
    }
    this.companyId = companyId || '';
    this.isCompanyDropdownOpen = false;
    this.locationId = '';
    this.locationOptions = [];
    if (companyId) {
      this.loadLocations(companyId);
    }
  }

  toggleLocationDropdown(): void {
    if (this.lockCompanyLocation) {
      return;
    }
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    if (this.isLocationDropdownOpen) {
      this.closeOtherDropdowns('location');
    }
  }

  closeLocationDropdown(): void {
    this.isLocationDropdownOpen = false;
  }

  selectLocation(locationId: string): void {
    if (this.lockCompanyLocation) {
      return;
    }
    this.locationId = locationId || '';
    this.isLocationDropdownOpen = false;
  }

  toggleStatusDropdown(): void {
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
    if (this.isStatusDropdownOpen) {
      this.closeOtherDropdowns('status');
    }
  }

  closeStatusDropdown(): void {
    this.isStatusDropdownOpen = false;
  }

  selectStatus(status: string): void {
    this.status = status === 'inactive' ? 'inactive' : 'active';
    this.isStatusDropdownOpen = false;
  }

  toggleCameraStatusDropdown(): void {
    this.isCameraStatusDropdownOpen = !this.isCameraStatusDropdownOpen;
    if (this.isCameraStatusDropdownOpen) {
      this.closeOtherDropdowns('cameraStatus');
    }
  }

  closeCameraStatusDropdown(): void {
    this.isCameraStatusDropdownOpen = false;
  }

  selectCameraStatus(status: string): void {
    this.cameraStatus = status === 'inactive' ? 'inactive' : 'active';
    this.isCameraStatusDropdownOpen = false;
  }

  toggleGateTypeDropdown(): void {
    this.isGateTypeDropdownOpen = !this.isGateTypeDropdownOpen;
    if (this.isGateTypeDropdownOpen) {
      this.closeOtherDropdowns('gateType');
    }
  }

  closeGateTypeDropdown(): void {
    this.isGateTypeDropdownOpen = false;
  }

  selectGateType(type: string): void {
    this.laneType = type === 'manual' ? 'manual' : 'automatic';
    this.isGateTypeDropdownOpen = false;
    this.gateRelayError = '';
    this.gateSerialError = '';
    this.transactionTypeError = '';
    if (this.laneType === 'automatic' && this.transactionTypeIds.length === 0) {
      this.transactionTypeIds = [...ALL_LANE_TRANSACTION_TYPE_IDS];
    }
  }

  onTransactionTypesChange(ids: string[]): void {
    this.transactionTypeIds = ids;
    this.transactionTypeError = '';
  }

  onRelayChange(): void {
    this.gateRelay = digitsOnly(this.gateRelay);
    this.gateRelayError = '';
    this.checkClosePortDiffersFromOpen();
  }

  onCloseGateEnabledChange(): void {
    this.gateCloseRelayError = '';
    if (this.closeGateEnabled && !this.gateCloseRelay.trim()) {
      this.gateCloseRelay = DEFAULT_GATE_CLOSE_PORT;
    }
    this.checkClosePortDiffersFromOpen();
  }

  onCloseRelayChange(): void {
    this.gateCloseRelay = digitsOnly(this.gateCloseRelay);
    this.gateCloseRelayError = '';
    this.checkClosePortDiffersFromOpen();
  }

  /** The open relay port can never close the gate: flag it as soon as both match. */
  private checkClosePortDiffersFromOpen(): void {
    const closePort = parseGatePort(this.gateCloseRelay);
    const sameAsOpen =
      this.closeGateEnabled && closePort !== null && closePort === parseGatePort(this.gateRelay);
    if (sameAsOpen) {
      this.gateCloseRelayError = CLOSE_PORT_SAME_AS_OPEN;
    } else if (this.gateCloseRelayError === CLOSE_PORT_SAME_AS_OPEN) {
      this.gateCloseRelayError = '';
    }
  }

  onCameraSerialChange(): void {
    this.cameraSerialError = '';
    clearTimeout(this.cameraSerialCheckDebounce);
    this.cameraSerialCheckDebounce = setTimeout(() => {
      this.checkDeviceConnection('camera');
    }, 500);
  }

  onGateSerialChange(): void {
    this.gateSerialError = '';
    clearTimeout(this.gateSerialCheckDebounce);
    this.gateSerialCheckDebounce = setTimeout(() => {
      this.checkDeviceConnection('gate');
    }, 500);
  }

  private checkDeviceConnection(deviceType: 'camera' | 'gate'): void {
    const serial = deviceType === 'camera' ? this.cameraSerialNumber : this.gateSerialNumber;

    // Cancel previous in-flight request for this device type
    if (deviceType === 'camera') {
      this.cameraConnectionSubscription?.unsubscribe();
    } else {
      this.gateConnectionSubscription?.unsubscribe();
    }

    if (!serial?.trim()) {
      if (deviceType === 'camera') {
        this.isCameraConnected = false;
        this.isCheckingCameraConnection = false;
        this.cameraConnectionSubscription = null;
      } else {
        this.isGateConnected = false;
        this.isCheckingGateConnection = false;
        this.gateConnectionSubscription = null;
      }
      return;
    }

    if (deviceType === 'camera') {
      this.isCheckingCameraConnection = true;
    } else {
      this.isCheckingGateConnection = true;
    }

    const subscription = this.laneService.checkDeviceConnection(serial.trim()).subscribe({
      next: response => {
        if (deviceType === 'camera') {
          this.isCameraConnected = response.data?.connected ?? false;
          this.isCheckingCameraConnection = false;
          this.cameraConnectionSubscription = null;
        } else {
          this.isGateConnected = response.data?.connected ?? false;
          this.isCheckingGateConnection = false;
          this.gateConnectionSubscription = null;
        }
      },
      error: () => {
        if (deviceType === 'camera') {
          this.isCameraConnected = false;
          this.isCheckingCameraConnection = false;
          this.cameraConnectionSubscription = null;
        } else {
          this.isGateConnected = false;
          this.isCheckingGateConnection = false;
          this.gateConnectionSubscription = null;
        }
      },
    });

    // Track the subscription so we can cancel it if needed
    if (deviceType === 'camera') {
      this.cameraConnectionSubscription = subscription;
    } else {
      this.gateConnectionSubscription = subscription;
    }
  }

  private validateForm(): boolean {
    this.laneNameError = '';
    this.cameraSerialError = '';
    this.gateRelayError = '';
    this.gateCloseRelayError = '';
    this.gateSerialError = '';
    this.transactionTypeError = '';

    if (!this.laneName.trim()) {
      this.laneNameError = 'Lane name is required';
    }
    if (this.hasCamera && !this.cameraSerialNumber.trim()) {
      this.cameraSerialError = 'Serial number is required';
    }
    if (this.hasGate) {
      this.gateRelayError = gatePortError(this.gateRelay);
      if (!this.gateSerialNumber.trim()) {
        this.gateSerialError = 'Serial number is required';
      }
      if (this.laneType === 'automatic' && this.transactionTypeIds.length === 0) {
        this.transactionTypeError = 'Select at least one transaction type';
      }
      if (this.closeGateEnabled) {
        this.gateCloseRelayError = gatePortError(this.gateCloseRelay);
        if (!this.gateCloseRelayError) {
          this.checkClosePortDiffersFromOpen();
        }
      }
    }

    return !(
      this.laneNameError ||
      this.cameraSerialError ||
      this.gateRelayError ||
      this.gateCloseRelayError ||
      this.gateSerialError ||
      this.transactionTypeError
    );
  }

  onSubmit(): void {
    if (!this.validateForm() || !this.companyId || !this.locationId) {
      return;
    }

    const formValues: LaneFormValues = {
      laneName: this.laneName,
      hasCamera: this.hasCamera,
      cameraSerialNumber: this.cameraSerialNumber,
      cameraModel: this.cameraModel,
      cameraStatus: this.cameraStatus,
      zone: this.zone,
      hasGate: this.hasGate,
      laneType: this.laneType,
      gateRelay: this.gateRelay,
      gateSerialNumber: this.gateSerialNumber,
      gateModel: this.gateModel,
      closeGateEnabled: this.closeGateEnabled,
      gateCloseRelay: this.gateCloseRelay,
      status: this.status,
      transactionTypeIds: [...this.transactionTypeIds],
    };
    const payload = buildLanePayload(formValues, this.mode === 'edit' ? 'update' : 'create');

    this.isSubmitting = true;
    const request$ =
      this.mode === 'edit' && this.laneId
        ? this.laneService.updateLane(this.companyId, this.locationId, this.laneId, payload)
        : this.laneService.createLane(this.companyId, this.locationId, payload);

    request$.subscribe({
      next: response => {
        this.isSubmitting = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.mode === 'edit' ? 'Lane updated successfully' : 'Lane added successfully',
          });
          this.saved.emit({ companyId: this.companyId, locationId: this.locationId });
          this.close.emit();
        } else {
          this.showSaveError(response.message || 'Failed to save lane');
        }
      },
      error: err => {
        this.isSubmitting = false;
        this.showSaveError(err?.error?.message || 'Failed to save lane');
      },
    });
  }

  private showSaveError(message: string): void {
    this.laneNameError = '';
    this.cameraSerialError = '';
    this.gateSerialError = '';
    this.gateRelayError = '';
    this.gateCloseRelayError = '';
    this.transactionTypeError = '';

    const mapped = mapLaneSaveError(message);
    if (mapped.field === 'laneName') {
      this.laneNameError = mapped.message;
    } else if (mapped.field === 'cameraSerial') {
      this.cameraSerialError = mapped.message;
    } else if (mapped.field === 'gateSerial') {
      this.gateSerialError = mapped.message;
    } else if (mapped.field === 'gateCloseRelay' || this.isCloseRelayConflict(mapped.message)) {
      this.gateCloseRelayError = mapped.message;
    } else if (mapped.field === 'gateRelay') {
      this.gateRelayError = mapped.message;
    }

    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mapped.message,
    });
  }

  /** "Relay N on gate serial … is already used by lane …" naming the close relay. */
  private isCloseRelayConflict(message: string): boolean {
    const closePort = parseGatePort(this.gateCloseRelay);
    return (
      this.closeGateEnabled &&
      closePort !== null &&
      message.toLowerCase().startsWith(`relay ${closePort} on gate serial`)
    );
  }

  private loadLocations(companyId: string, preserveLocation = false): void {
    this.apiService
      .getWithSuperAdminToken<any[]>(`admin/super-admin/companies/${companyId}/locations`)
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            const locations = Array.isArray(response.data) ? response.data : [];
            this.locationOptions = locations.map((location: any) => ({
              label: location.branch || location.name || location.id,
              value: location.id,
            }));

            if (preserveLocation && this.initialLocationId) {
              const exists = this.locationOptions.some(opt => opt.value === this.initialLocationId);
              this.locationId = exists
                ? this.initialLocationId
                : (this.locationOptions[0]?.value ?? '');
            } else if (this.locationOptions.length > 0 && !this.locationId) {
              this.locationId = this.locationOptions[0].value;
            }
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load locations',
          });
        },
      });
  }
}
